/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Logger } from '@app/core';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { DataStateChangeEvent, GridDataResult, RowClassArgs } from '@progress/kendo-angular-grid';
import { ApiQuery } from '@app/core/http/api-query';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { HotelBookingsService } from '@app/hotels/hotel-bookings/hotel-bookings.service';
import { HotelBookingsFormService } from '@app/hotels/hotel-bookings/hotel-bookings-form.service';
import { HotelBooking } from '@app/hotels/hotel-bookings/hotel-booking.model';
import { environment } from '@env/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { DataSet } from '@app/shared/kendo/data-set';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { AccessService } from '@app/core/access/access.service';
import { Room } from '@app/hotels/rooms/room.model';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const log = new Logger('HotelBookingsListComponent');

@UntilDestroy()
@Component({
  selector: 'app-hotel-bookings-list',
  templateUrl: './hotel-bookings-list.component.html',
  styleUrls: ['./hotel-bookings-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HotelBookingsListComponent extends KendoGridComponent implements OnInit {
  hotelBookingsResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  env = environment;
  eventId: number;
  LaravelModelsEnum = LaravelModelsEnum;
  MediaLibraryAcceptTypes = MediaLibraryAcceptTypes;
  UserRoleType = UserRoleType;
  roomRefNumFilter = false;

  constructor(
    private hotelBookingService: HotelBookingsService,
    private hotelBookingsFormService: HotelBookingsFormService,
    protected kendoGridService: KendoGridService,
    private route: ActivatedRoute,
    private router: Router,
    public accessService: AccessService,
    private cdr: ChangeDetectorRef
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    this.route.queryParamMap.pipe(untilDestroyed(this)).subscribe((params) => {
      this.state = CustomKendoFilter.getStateFromStorage('HotelBookingsListComponent');

      if (params.get('event_id')) {
        this.state = new DataSet().addFilter('events.id', Number(params.get('event_id')));
      }

      if (params.get('dataSet')) {
        this.state = DataSet.decodeFromJson(params.get('dataSet'));
      }

      this.loadData(this.state);
    });

    // this.loadData(this.state);
  }

  /**
   * Load data from API
   */
  loadData(state: DataStateChangeEvent = null) {
    this.state = state;

    // @ts-ignore
    const inBoc = this.state?.filter?.filters[0]?.field === 'assignmentToCancellation';

    this.apiQuery
      .setDataSetFilters(state)
      .addIncludes(
        'events',
        'rooms.type',
        'hotel',
        'supplier',
        'company',
        'property',
        'available_nights_count',
        'cancellations_count',
        'breakfast_price_per_night_currency',
        'tags',
        'contracting_agent',
        'reservation_agent',
        'amendment_agent'
      );

    if (inBoc) {
      this.apiQuery.addInclude('rooms.saleFinalItems');
    }

    this.hotelBookingService
      .index(this.apiQuery)
      .pipe(untilDestroyed(this))
      .subscribe((response: PaginatedResponse) => {
        if (inBoc) {
          response.data.forEach((booking: HotelBooking) => {
            booking['meta_front'] = {};
            booking['meta_front']['isBookingHasFinal'] = false;
            booking.rooms.forEach((room: Room) => {
              if (room.sale_final_items.length > 0) {
                booking['meta_front']['isBookingHasFinal'] = true;
              }
            });
          });
        }

        this.hotelBookingsResponse = response;
        this.data = response.getGridData();
        this.cdr.markForCheck();
      });

    CustomKendoFilter.pushStateInStorage('HotelBookingsListComponent', this.state);

    this.roomRefNumFilter = false;
    if (this.state && this.state.filter && this.state.filter.filters) {
      this.state.filter.filters.forEach((filter: any) => {
        if (filter.field === 'rooms.ref_number') {
          this.roomRefNumFilter = true;
        }
      });
    }
  }

  bookingRowCallback(context: RowClassArgs) {
    return {
      'kendo-row-has-final': context?.dataItem?.meta_front?.isBookingHasFinal,
    };
  }

  clearFilters() {
    this.router.navigate(['/hotel-bookings']);

    this.apiQuery = new ApiQuery();
    this.loadData();
  }

  add() {
    this.hotelBookingsFormService.open().then(
      (response) => {
        this.loadData(this.state);
      },
      (err) => {
        log.info('modal closed');
      }
    );
  }

  edit(hotelBooking: HotelBooking) {
    this.hotelBookingsFormService.open(hotelBooking).then(
      (response) => {
        this.loadData(this.state);
      },
      (err) => {
        // this.loadData(this.state);
        this.loadData(this.state);
      }
    );
  }

  /**
   * @param hotelBooking HotelBooking object
   */
  delete(hotelBooking: HotelBooking) {
    this.hotelBookingService
      .destroy(hotelBooking.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.loadData(this.state);
      });
  }

  test(aa: any) {
    log.debug(aa);
  }
}
